/* Reusable primitives with Apple-glass style. */
import { X, Plus, Trash2, Pencil, ChevronRight } from "lucide-react";
import { useState } from "react";

export function DataTable({ columns, rows, actions, testid, expandable, expandedRow, onToggleExpand, rowClassName, rowProps }) {
  return (
    <div className="wv-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/60 text-[var(--wv-text-2)]">
            {expandable && <th className="py-3 px-2 w-8"></th>}
            {columns.map((c) => (
              <th key={c.key} className="py-3 px-3 text-xs uppercase tracking-wider font-semibold whitespace-nowrap">{c.label}</th>
            ))}
            {actions && <th className="py-3 px-3 text-xs uppercase tracking-wider font-semibold w-28"></th>}
          </tr>
        </thead>
        <tbody>
          {(rows || []).length === 0 && (
            <tr><td className="py-10 px-4 text-center text-[var(--wv-text-3)]" colSpan={columns.length + (actions ? 1 : 0) + (expandable ? 1 : 0)}>No records.</td></tr>
          )}
          {(rows || []).map((r, i) => {
            const isOpen = expandable && expandedRow === (r.id || i);
            return (
              <ExpandableRow
                key={r.id || i}
                row={r}
                columns={columns}
                actions={actions}
                expandable={expandable}
                isOpen={isOpen}
                onToggle={() => onToggleExpand && onToggleExpand(r.id || i)}
                extraRowClass={typeof rowClassName === "function" ? rowClassName(r) : ""}
                extraRowProps={typeof rowProps === "function" ? rowProps(r) : {}}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExpandableRow({ row, columns, actions, expandable, isOpen, onToggle, extraRowClass = "", extraRowProps = {} }) {
  return (
    <>
      <tr className={`border-b border-white/50 hover:bg-white/50 transition-colors ${extraRowClass}`} {...extraRowProps}>
        {expandable && (
          <td className="py-2.5 px-2 w-8">
            <button onClick={onToggle} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]" data-testid={`row-expand-${row.id || ""}`}>
              <ChevronRight size={16} className={`transition-transform ${isOpen ? "rotate-90" : ""}`}/>
            </button>
          </td>
        )}
        {columns.map((c) => (
          <td key={c.key} className={`py-2.5 px-3 text-sm whitespace-nowrap ${c.mono ? "font-mono" : ""}`}>{c.render ? c.render(row) : row[c.key]}</td>
        ))}
        {actions && <td className="py-2.5 px-3 text-sm whitespace-nowrap">{actions(row)}</td>}
      </tr>
      {isOpen && expandable && (
        <tr className="bg-white/30">
          <td colSpan={columns.length + (actions ? 1 : 0) + 1} className="py-4 px-5">
            {expandable(row)}
          </td>
        </tr>
      )}
    </>
  );
}

export function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-[rgba(20,28,40,0.35)] backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wv-card w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/50">
          <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>{title}</h3>
          <button onClick={onClose} className="text-[var(--wv-text-2)] hover:text-[var(--wv-text)]"><X size={18}/></button>
        </div>
        <div className="p-6">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-white/50 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function Btn({ children, variant = "primary", ...props }) {
  const cls = variant === "primary"
    ? "wv-btn-primary"
    : variant === "danger"
    ? "wv-btn-danger"
    : variant === "ghost"
    ? "bg-transparent hover:bg-white/60 text-[var(--wv-text-2)]"
    : "wv-btn-secondary";
  return (
    <button {...props} className={`h-10 px-4 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed ${cls} ${props.className || ""}`}>{children}</button>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-[0.68rem] uppercase tracking-widest text-[var(--wv-text-2)] font-semibold">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function TextInput(props) {
  return (
    <input
      {...props}
      className={`w-full h-10 px-3.5 wv-input text-sm ${props.className || ""}`}
    />
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={`w-full h-10 px-3 wv-input text-sm ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-3.5 py-2.5 wv-input text-sm ${props.className || ""}`}
    />
  );
}

export function Badge({ children, tone = "default" }) {
  const map = {
    default: "bg-white/80 text-[var(--wv-text-2)] border border-white/70",
    success: "bg-[rgba(220,230,219,0.9)] text-[var(--wv-primary)] border border-white/70",
    warning: "bg-[rgba(251,236,199,0.9)] text-[#8A6D2C] border border-white/70",
    danger: "bg-[rgba(244,214,206,0.9)] text-[var(--wv-error)] border border-white/70",
    info: "bg-[rgba(222,228,236,0.9)] text-[var(--wv-info)] border border-white/70",
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[tone] || map.default}`}>{children}</span>;
}

export function useToggle(initial = false) {
  const [v, setV] = useState(initial);
  return [v, () => setV((x) => !x), setV];
}

export const Icons = { Plus, Trash2, Pencil };
